import React, { createContext, useContext, useState, useCallback } from 'react';

const DashboardContext = createContext(null);

export const DashboardProvider = ({ children }) => {
  // Active tab state
  const [activeTab, setActiveTab] = useState('portfolio');
  
  // Global filters
  const [filters, setFilters] = useState({
    asOnDate: null,
    branch: null,
  });
  
  // Data cache for each tab
  const [dataCache, setDataCache] = useState({
    portfolio: null,
    risk: null,
    deposits: null,
    psl: null,
    customers: null,
  });
  
  // Loading states
  const [loading, setLoading] = useState({
    portfolio: false,
    risk: false,
    deposits: false,
    psl: false,
    customers: false,
  });
  
  // Error states
  const [errors, setErrors] = useState({
    portfolio: null,
    risk: null,
    deposits: null,
    psl: null,
    customers: null,
  });

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    // Clear cache when filters change
    setDataCache({
      portfolio: null,
      risk: null,
      deposits: null,
      psl: null,
      customers: null,
    });
  }, []);

  // Set tab data
  const setTabData = useCallback((tabKey, data) => {
    setDataCache(prev => ({ ...prev, [tabKey]: data }));
  }, []);

  // Set loading state
  const setTabLoading = useCallback((tabKey, isLoading) => {
    setLoading(prev => ({ ...prev, [tabKey]: isLoading }));
  }, []);

  // Set error state
  const setTabError = useCallback((tabKey, error) => {
    setErrors(prev => ({ ...prev, [tabKey]: error }));
  }, []);

  // Check if tab data is cached
  const isTabCached = useCallback((tabKey) => {
    return dataCache[tabKey] !== null;
  }, [dataCache]);

  const value = {
    activeTab,
    setActiveTab,
    filters,
    updateFilters,
    dataCache,
    setTabData,
    loading,
    setTabLoading,
    errors,
    setTabError,
    isTabCached,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

export default DashboardContext;
